import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useQuery, useAction, useMutation } from "convex/react";
import {
  Search,
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Check,
  Globe,
  Lock,
  FileText,
  Github,
  Loader2,
  AlertCircle,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/lib/convex-api";
import { organizationRoute, projectRoute } from "@/lib/app-routes";
import {
  canProceedInNewProjectFlow,
  type NewProjectSourceType,
} from "./new-project-flow";
import { selectGitHubInstallationsForOrganization } from "./github-installation-selection";
import type { Id } from "../../../../backend/convex/_generated/dataModel";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Repo {
  id: number;
  fullName: string;
  name: string;
  owner: string;
  isPrivate: boolean;
  defaultBranch: string;
  description: string | null;
}

interface RepoOption extends Repo {
  installationId: number;
}

interface FolderNode {
  id: string;
  path: string;
  name: string;
  depth: number;
  children: FolderNode[];
  mdFileCount: number;
  driveId?: string;
  driveKind?: "MY_DRIVE" | "SHARED_DRIVE";
}

interface DriveConnection {
  id: Id<"googleDriveConnections">;
  email: string | null;
  displayName: string | null;
}

const buildGoogleDriveStartUrl = (state: string) => {
  const convexUrl = import.meta.env.VITE_CONVEX_URL?.trim();
  if (!convexUrl) {
    throw new Error("Missing VITE_CONVEX_URL for Google Drive OAuth.");
  }
  return `${convexUrl.replace(/\/+$/, "")}/api/google-drive/oauth/start?state=${encodeURIComponent(
    state,
  )}`;
};

// ---------------------------------------------------------------------------
// GitHub App Install prompt
// ---------------------------------------------------------------------------

const GITHUB_APP_NAME = import.meta.env.VITE_GITHUB_APP_NAME ?? "mdsync";

const InstallGitHubApp = ({
  organizationId,
}: {
  organizationId: Id<"workspaces">;
}) => {
  const createInstallState = useMutation(api.github.createInstallState);

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-muted">
        <Github className="size-7 text-muted-foreground" />
      </div>
      <h2 className="text-lg font-semibold">Connect GitHub</h2>
      <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
        Install the mdsync GitHub App to give us access to the repositories you
        want to sync. You choose exactly which repos to share.
      </p>
      <Button
        className="mt-6"
        onClick={async () => {
          const state = await createInstallState({
            organizationId,
          });
          const installUrl = `https://github.com/apps/${GITHUB_APP_NAME}/installations/new?state=${encodeURIComponent(
            state,
          )}`;
          globalThis.location.assign(installUrl);
        }}
      >
        <Github className="size-4" />
        Install GitHub App
      </Button>
    </div>
  );
};

const InstallGoogleDrive = ({
  organizationId,
}: {
  organizationId: Id<"workspaces">;
}) => {
  const createOauthState = useMutation(api.googleDrive.createOauthState);

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-muted">
        <FolderOpen className="size-7 text-muted-foreground" />
      </div>
      <h2 className="text-lg font-semibold">Connect Google Drive</h2>
      <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
        Connect your Google Drive account once, then pick the folder this
        project should sync. Collaborators will use your connection and do not
        need Google accounts.
      </p>
      <Button
        className="mt-6"
        onClick={async () => {
          const state = await createOauthState({
            organizationId,
          });
          globalThis.location.assign(buildGoogleDriveStartUrl(state));
        }}
      >
        <FolderOpen className="size-4" />
        Connect Google Drive
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 1: Connect Repository
// ---------------------------------------------------------------------------

const StepConnectRepo = ({
  installations,
  selectedRepo,
  onSelect,
  organizationId,
}: {
  installations: Array<{
    id: string;
    installationId: number;
    accountLogin: string;
    accountType: string;
    repositorySelection: string;
  }>;
  selectedRepo: Repo | null;
  onSelect: (repo: Repo, installationId: number) => void;
  organizationId: Id<"workspaces">;
}) => {
  const [search, setSearch] = useState("");
  const [repos, setRepos] = useState<RepoOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const listRepos = useAction(api.githubActions.listInstallationRepos);
  const createInstallState = useMutation(api.github.createInstallState);

  // Fetch repos from all installations
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const allRepos: RepoOption[] = [];
        for (const inst of installations) {
          const result = await listRepos({
            installationId: inst.installationId,
            perPage: 100,
          });
          for (const r of result.repos) {
            allRepos.push({
              ...r,
              installationId: inst.installationId,
            } as RepoOption);
          }
        }
        if (!cancelled) {
          // Deduplicate by id, sort alphabetically
          const unique = Array.from(
            new Map(allRepos.map((r) => [r.id, r])).values(),
          ).sort((a, b) => a.fullName.localeCompare(b.fullName));
          setRepos(unique);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to fetch repositories",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [installations, listRepos]);

  const filtered = repos.filter((r) =>
    r.fullName.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Connect a repository</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose an existing repository to connect to this project.
        </p>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          placeholder="Search repositories..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>
      <ScrollArea className="h-[340px] w-full rounded-md border">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              Loading repositories...
            </span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
            <AlertCircle className="size-5 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : (
          <div className="min-w-0 w-full divide-y">
            {filtered.map((repo) => {
              const isSelected = selectedRepo?.id === repo.id;
              return (
                <button
                  key={repo.id}
                  onClick={() => onSelect(repo, repo.installationId)}
                  className={cn(
                    "flex min-w-0 w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50",
                    isSelected && "bg-accent",
                  )}
                >
                  <Folder className="size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <span className="block truncate text-sm">
                      {repo.fullName}
                    </span>
                    {repo.description && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {repo.description}
                      </span>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className="shrink-0 gap-1 text-[10px]"
                  >
                    {repo.isPrivate ? (
                      <>
                        <Lock className="size-2.5" />
                        Private
                      </>
                    ) : (
                      <>
                        <Globe className="size-2.5" />
                        Public
                      </>
                    )}
                  </Badge>
                  {isSelected && (
                    <Check className="size-4 shrink-0 text-foreground" />
                  )}
                </button>
              );
            })}
            {filtered.length === 0 && !loading && (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                No repositories found.
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      <p className="text-xs text-muted-foreground">
        Don&apos;t see your repo?{" "}
        <a
          href="#"
          className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground"
          onClick={(event) => {
            event.preventDefault();
            void (async () => {
              const state = await createInstallState({
                organizationId,
              });
              const manageUrl = `https://github.com/apps/${GITHUB_APP_NAME}/installations/new?state=${encodeURIComponent(
                state,
              )}`;
              window.open(manageUrl, "_blank", "noopener,noreferrer");
            })();
          }}
        >
          Update installation permissions on GitHub
          <ExternalLink className="size-3" />
        </a>
      </p>
    </div>
  );
};

const StepConnectGoogleDrive = ({
  connections,
  selectedConnectionId,
  onSelect,
  organizationId,
}: {
  connections: DriveConnection[];
  selectedConnectionId: Id<"googleDriveConnections"> | null;
  onSelect: (connection: DriveConnection) => void;
  organizationId: Id<"workspaces">;
}) => {
  const createOauthState = useMutation(api.googleDrive.createOauthState);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Choose a Drive connection</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          This project will sync through the selected owner connection.
          Collaborators will work through the app without signing into Google
          Drive.
        </p>
      </div>
      <div className="rounded-md border">
        {connections.map((connection) => {
          const isSelected = selectedConnectionId === connection.id;
          const label =
            connection.displayName || connection.email || "Google Drive";
          const subtitle =
            connection.email && connection.displayName
              ? connection.email
              : "Owner-backed connection";
          return (
            <button
              key={connection.id}
              onClick={() => onSelect(connection)}
              className={cn(
                "flex w-full items-center gap-3 border-b px-3 py-3 text-left last:border-b-0 hover:bg-muted/50",
                isSelected && "bg-accent",
              )}
            >
              <FolderOpen className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{label}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {subtitle}
                </div>
              </div>
              {isSelected && (
                <Check className="size-4 shrink-0 text-foreground" />
              )}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        Need another account?{" "}
        <a
          href="#"
          className="underline underline-offset-2 hover:text-foreground"
          onClick={(event) => {
            event.preventDefault();
            void (async () => {
              const state = await createOauthState({
                organizationId,
              });
              window.open(
                buildGoogleDriveStartUrl(state),
                "_blank",
                "noopener,noreferrer",
              );
            })();
          }}
        >
          Connect another Google Drive account
        </a>
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Folder Tree Node (recursive)
// ---------------------------------------------------------------------------

const FolderTreeNode = ({
  node,
  selectedFolder,
  onSelect,
  expandedPaths,
  onToggleExpand,
}: {
  node: FolderNode;
  selectedFolder: string;
  onSelect: (path: string) => void;
  expandedPaths: Set<string>;
  onToggleExpand: (path: string) => void;
}) => {
  const isSelected = selectedFolder === node.path;
  const isExpanded = expandedPaths.has(node.path);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      <button
        onClick={() => {
          onSelect(node.path);
          if (hasChildren) onToggleExpand(node.path);
        }}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted/50",
          isSelected && "bg-accent",
        )}
        style={{ paddingLeft: `${node.depth * 16 + 12}px` }}
      >
        {hasChildren ? (
          isExpanded ? (
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          )
        ) : (
          <span className="size-3.5 shrink-0" />
        )}
        {isSelected ? (
          <FolderOpen className="size-4 shrink-0 text-foreground" />
        ) : (
          <Folder className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span
          className={cn(
            isSelected
              ? "font-medium text-foreground"
              : "text-muted-foreground",
          )}
        >
          {node.name}
        </span>
        {isSelected && (
          <Check className="ml-auto size-4 shrink-0 text-foreground" />
        )}
      </button>
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <FolderTreeNode
              key={child.path}
              node={child}
              selectedFolder={selectedFolder}
              onSelect={onSelect}
              expandedPaths={expandedPaths}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DriveFolderTreeNode = ({
  node,
  selectedFolderId,
  onSelect,
  expandedIds,
  onToggleExpand,
}: {
  node: FolderNode;
  selectedFolderId: string;
  onSelect: (node: FolderNode) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
}) => {
  const isSelected = selectedFolderId === node.id;
  const isExpanded = expandedIds.has(node.id);
  const hasChildren = node.children.length > 0;

  return (
    <div>
      <button
        onClick={() => {
          onSelect(node);
          if (hasChildren) onToggleExpand(node.id);
        }}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted/50",
          isSelected && "bg-accent",
        )}
        style={{ paddingLeft: `${node.depth * 16 + 12}px` }}
      >
        {hasChildren ? (
          isExpanded ? (
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          )
        ) : (
          <span className="size-3.5 shrink-0" />
        )}
        {isSelected ? (
          <FolderOpen className="size-4 shrink-0 text-foreground" />
        ) : (
          <Folder className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span
          className={cn(isSelected ? "font-medium" : "text-muted-foreground")}
        >
          {node.name}
        </span>
        {isSelected && (
          <Check className="ml-auto size-4 shrink-0 text-foreground" />
        )}
      </button>
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <DriveFolderTreeNode
              key={child.id}
              node={child}
              selectedFolderId={selectedFolderId}
              onSelect={onSelect}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 2: Choose Folder & Create
// ---------------------------------------------------------------------------

const StepChooseFolderCreate = ({
  repo,
  installationId,
  selectedFolder,
  onSelectFolder,
  projectName,
  onNameChange,
}: {
  repo: Repo;
  installationId: number;
  selectedFolder: string;
  onSelectFolder: (path: string) => void;
  projectName: string;
  onNameChange: (name: string) => void;
}) => {
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(
    new Set(["/"]),
  );
  const [folderTree, setFolderTree] = useState<FolderNode[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  const getRepoTree = useAction(api.githubActions.getRepoTree);

  useEffect(() => {
    let cancelled = false;
    const addIds = (nodes: Array<Omit<FolderNode, "id">>): FolderNode[] =>
      nodes.map((node) => ({
        ...node,
        id: node.path,
        children: addIds(node.children as Array<Omit<FolderNode, "id">>),
      }));
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await getRepoTree({
          installationId,
          owner: repo.owner,
          repo: repo.name,
          branch: repo.defaultBranch,
        });
        if (!cancelled) {
          setFolderTree(addIds(result.tree as Array<Omit<FolderNode, "id">>));
          setTruncated(result.truncated);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load folder tree",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [installationId, repo, getRepoTree]);

  const isRootOnlyTree =
    !loading &&
    !error &&
    folderTree != null &&
    folderTree.length === 1 &&
    folderTree[0]?.path === "/" &&
    folderTree[0]?.children?.length === 0;

  const onToggleExpand = (path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  // Find selected folder's md count
  const findNode = (nodes: FolderNode[], path: string): FolderNode | null => {
    for (const node of nodes) {
      if (node.path === path) return node;
      if (node.children) {
        const found = findNode(node.children, path);
        if (found) return found;
      }
    }
    return null;
  };

  const selectedNode = folderTree ? findNode(folderTree, selectedFolder) : null;
  const mdCount = selectedNode?.mdFileCount ?? 0;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Choose your docs folder</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Select the folder that contains your Markdown documents in{" "}
          <span className="font-medium text-foreground">
            {repo.owner}/{repo.name}
          </span>
          .
        </p>
      </div>

      {/* Folder tree */}
      {!isRootOnlyTree && (
        <div className="rounded-md border">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                Loading folder structure...
              </span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
              <AlertCircle className="size-5 text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          ) : (
            folderTree?.map((node) => (
              <FolderTreeNode
                key={node.path}
                node={node}
                selectedFolder={selectedFolder}
                onSelect={onSelectFolder}
                expandedPaths={expandedPaths}
                onToggleExpand={onToggleExpand}
              />
            ))
          )}
        </div>
      )}

      {isRootOnlyTree && (
        <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
          This repository has no folders yet. We&apos;ll sync the whole
          repository.
        </div>
      )}

      {!isRootOnlyTree && truncated && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          This repository is very large. Some folders may not be shown.
        </p>
      )}

      {/* Folder info */}
      {!isRootOnlyTree && !loading && !error && (
        <div className="space-y-1 text-sm">
          <p className="text-muted-foreground">
            Selected:{" "}
            <span className="font-medium text-foreground">
              {selectedFolder}
            </span>
          </p>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <FileText className="size-3.5" />
            <span>{mdCount} Markdown files in this folder</span>
          </div>
          {mdCount > 200 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              This folder has many files. You can fine-tune excluded folders in
              project settings after creation.
            </p>
          )}
        </div>
      )}

      {/* Project name */}
      <div className="space-y-2">
        <Label htmlFor="project-name">Project name</Label>
        <Input
          id="project-name"
          value={projectName}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="My Project"
        />
      </div>
    </div>
  );
};

const StepChooseDriveFolderCreate = ({
  connectionId,
  selectedFolderId,
  onSelectFolder,
  projectName,
  onNameChange,
}: {
  connectionId: Id<"googleDriveConnections">;
  selectedFolderId: string;
  onSelectFolder: (node: FolderNode) => void;
  projectName: string;
  onNameChange: (name: string) => void;
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [folderTree, setFolderTree] = useState<FolderNode[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const listConnectionFolders = useAction(
    api.googleDriveActions.listConnectionFolders,
  );

  const loadFolders = useCallback(
    async (isCancelled?: () => boolean) => {
      setLoading(true);
      setError(null);
      try {
        const result = await listConnectionFolders({ connectionId });
        if (isCancelled?.()) return;
        setFolderTree(result as FolderNode[]);
        const rootIds = (result as FolderNode[]).map((node) => node.id);
        setExpandedIds(new Set(rootIds));
      } catch (err) {
        if (isCancelled?.()) return;
        setError(
          err instanceof Error ? err.message : "Failed to load Drive folders",
        );
      } finally {
        if (!isCancelled?.()) {
          setLoading(false);
        }
      }
    },
    [connectionId, listConnectionFolders],
  );

  useEffect(() => {
    let cancelled = false;
    void loadFolders(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [loadFolders]);

  const findNode = (nodes: FolderNode[], id: string): FolderNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      const found = findNode(node.children, id);
      if (found) return found;
    }
    return null;
  };

  const selectedNode = folderTree
    ? findNode(folderTree, selectedFolderId)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Choose your Drive folder</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Select the Google Drive folder that this project should mirror. All
            synced files stay relative to this folder root.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            void loadFolders();
          }}
          disabled={loading}
        >
          <RefreshCw className={cn("size-4", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>
      <div className="rounded-md border">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              Loading Drive folders...
            </span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <AlertCircle className="size-5 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : (
          folderTree?.map((node) => (
            <DriveFolderTreeNode
              key={node.id}
              node={node}
              selectedFolderId={selectedFolderId}
              onSelect={onSelectFolder}
              expandedIds={expandedIds}
              onToggleExpand={(id) =>
                setExpandedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
            />
          ))
        )}
      </div>
      {selectedNode && (
        <div className="space-y-1 text-sm">
          <p className="text-muted-foreground">
            Selected:{" "}
            <span className="font-medium text-foreground">
              {selectedNode.path}
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            Collaborators use this owner-backed Drive connection and do not need
            Google accounts.
          </p>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="project-name-drive">Project name</Label>
        <Input
          id="project-name-drive"
          value={projectName}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="My Project"
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const NewProjectPage = () => {
  const navigate = useNavigate();
  const { orgSlug } = useParams({
    from: "/_authenticated/$orgSlug/new-project",
  });
  const searchParams = useSearch({
    from: "/_authenticated/$orgSlug/new-project",
  });
  const googleDriveConnected = (
    searchParams as { google_drive_connected?: string }
  ).google_drive_connected;

  const organizations = useQuery(api.organizations.listForUser, {});
  const currentOrganization = useMemo(() => {
    if (!organizations) return null;
    return (
      organizations.find((org: { slug: string }) => org.slug === orgSlug) ??
      null
    );
  }, [orgSlug, organizations]);
  const organizationId = currentOrganization?.id ?? null;
  const driveConnections = useQuery(
    api.googleDrive.listUserConnections,
    organizationId
      ? { organizationId: organizationId as Id<"workspaces"> }
      : ("skip" as any),
  );
  const workspaceInstallation = useQuery(
    api.github.getWorkspaceInstallation,
    organizationId
      ? { organizationId: organizationId as Id<"workspaces"> }
      : ("skip" as any),
  );

  const createProject = useMutation(api.projects.create);

  const preferredInstallationId = workspaceInstallation?.installationId ?? null;
  const githubInstallationState = selectGitHubInstallationsForOrganization(
    workspaceInstallation,
  );

  useEffect(() => {
    // #region agent log
    fetch("http://127.0.0.1:7244/ingest/5d66e8cd-209c-4ec8-8c1e-582897a80428", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        runId: "initial",
        hypothesisId: "H5",
        location: "new-project-page.tsx:NewProjectPage:workspaceInstallation",
        message: "Workspace installation query result",
        data: {
          organizationId,
          workspaceInstallationId:
            workspaceInstallation?.installationId ?? null,
          workspaceInstallationLogin:
            workspaceInstallation?.accountLogin ?? null,
          workspaceInstallationType: workspaceInstallation?.accountType ?? null,
          preferredInstallationId,
          installationsCount: githubInstallationState.installations.length,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
  }, [
    organizationId,
    workspaceInstallation,
    preferredInstallationId,
    githubInstallationState.installations.length,
  ]);

  useEffect(() => {
    // #region agent log
    fetch("http://127.0.0.1:7244/ingest/5d66e8cd-209c-4ec8-8c1e-582897a80428", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        runId: "initial",
        hypothesisId: "H6",
        location: "new-project-page.tsx:NewProjectPage:envAndOrgContext",
        message: "Environment and organization context snapshot",
        data: {
          organizationId,
          hasCurrentOrganization: Boolean(currentOrganization),
          currentOrganizationName: currentOrganization?.name ?? null,
          organizationsCount: organizations?.length ?? 0,
          convexUrl: import.meta.env.VITE_CONVEX_URL ?? null,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
  }, [organizationId, currentOrganization, organizations]);

  const [step, setStep] = useState(1);
  const [selectedRepo, setSelectedRepo] = useState<Repo | null>(null);
  const [selectedInstallationId, setSelectedInstallationId] = useState<
    number | null
  >(null);
  const [selectedFolder, setSelectedFolder] = useState("/");
  const [selectedDriveConnection, setSelectedDriveConnection] =
    useState<DriveConnection | null>(null);
  const [selectedDriveFolder, setSelectedDriveFolder] =
    useState<FolderNode | null>(null);
  const [projectName, setProjectName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [sourceType, setSourceType] = useState<NewProjectSourceType>("GITHUB");

  // If we just came back from GitHub App installation, auto-advance
  useEffect(() => {
    // #region agent log
    fetch("http://127.0.0.1:7244/ingest/8449b1a6-88f5-4593-85bb-2d072c02726b", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "c2d393",
      },
      body: JSON.stringify({
        sessionId: "c2d393",
        runId: "initial",
        hypothesisId: "H6",
        location: "new-project-page.tsx:NewProjectPage:routeState",
        message: "Loaded new project page route state",
        data: {
          organizationId,
          localStorageOrganizationId:
            globalThis.localStorage?.getItem("mdsync.selectedOrganizationId") ??
            null,
          hasErrorParam: Boolean(searchParams.error),
          errorPreview:
            typeof searchParams.error === "string"
              ? searchParams.error.slice(0, 120)
              : null,
          installationIdParam:
            typeof searchParams.installation_id === "string"
              ? searchParams.installation_id
              : null,
          setupActionParam:
            typeof searchParams.setup_action === "string"
              ? searchParams.setup_action
              : null,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
    if (searchParams.error) {
      console.error("GitHub installation error:", searchParams.error);
    }
  }, [
    organizationId,
    searchParams.error,
    searchParams.installation_id,
    searchParams.setup_action,
  ]);

  useEffect(() => {
    if (googleDriveConnected) {
      setSourceType("GOOGLE_DRIVE");
      setStep(1);
    }
  }, [googleDriveConnected]);

  const handleSelectRepo = useCallback((repo: Repo, installationId: number) => {
    setSelectedRepo(repo);
    setSelectedInstallationId(installationId);
    setProjectName(repo.name);
    setSelectedFolder("/");
  }, []);

  const handleCreate = async () => {
    if (!organizationId) return;
    if (
      sourceType === "GITHUB" &&
      (!selectedRepo || selectedInstallationId == null)
    )
      return;
    if (
      sourceType === "GOOGLE_DRIVE" &&
      (!selectedDriveConnection || !selectedDriveFolder)
    ) {
      return;
    }
    if (sourceType !== "GITHUB" && projectName.trim().length === 0) return;
    setIsCreating(true);
    try {
      const project = await (sourceType === "GITHUB"
        ? (() => {
            if (!selectedRepo || selectedInstallationId == null) {
              throw new Error("Missing repository selection.");
            }
            return createProject({
              organizationId: organizationId as Id<"workspaces">,
              sourceType: "GITHUB",
              installationId: selectedInstallationId,
              name: projectName.trim(),
              repoOwner: selectedRepo.owner,
              repoName: selectedRepo.name,
              branch: selectedRepo.defaultBranch,
              rootFolder: selectedFolder,
              excludedFolders: [],
            });
          })()
        : sourceType === "GOOGLE_DRIVE"
          ? (() => {
              if (!selectedDriveConnection || !selectedDriveFolder) {
                throw new Error("Missing Google Drive folder selection.");
              }
              return createProject({
                organizationId: organizationId as Id<"workspaces">,
                sourceType: "GOOGLE_DRIVE",
                connectionId: selectedDriveConnection.id,
                folderId: selectedDriveFolder.id,
                folderName: selectedDriveFolder.name,
                driveId: selectedDriveFolder.driveId,
                driveKind: selectedDriveFolder.driveKind ?? "MY_DRIVE",
                name: projectName.trim(),
                rootFolder: "/",
                excludedFolders: [],
              });
            })()
          : createProject({
              organizationId: organizationId as Id<"workspaces">,
              sourceType: "CLOUD",
              name: projectName.trim(),
              rootFolder: "/",
              excludedFolders: [],
            }));
      await navigate({
        to: projectRoute,
        params: { orgSlug: project.organizationSlug, id: project.slug },
      });
    } catch (err) {
      console.error("Failed to create project:", err);
      setIsCreating(false);
    }
  };

  const hasInstallations = githubInstallationState.hasLinkedInstallation;
  const isLoadingInstallations = githubInstallationState.isLoading;
  const hasDriveConnections =
    driveConnections !== undefined && driveConnections.length > 0;

  const canProceed = canProceedInNewProjectFlow({
    sourceType,
    step,
    hasSelectedRepo:
      sourceType === "GOOGLE_DRIVE"
        ? selectedDriveConnection !== null
        : selectedRepo !== null,
    projectName,
  });

  if (
    (sourceType === "GITHUB" && isLoadingInstallations) ||
    (sourceType === "GOOGLE_DRIVE" && driveConnections === undefined)
  ) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="h-full overflow-x-hidden overflow-y-auto">
        <div className="mx-auto max-w-xl px-4 py-6 sm:px-6">
          <div className="mb-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to={organizationRoute} params={{ orgSlug }}>
                <ChevronLeft className="size-4" />
                Back to Dashboard
              </Link>
            </Button>
          </div>
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            Missing organization context for project creation. Go back to the
            dashboard and click New Project from within the organization you
            want to create this project under.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-x-hidden overflow-y-auto">
      <div className="mx-auto max-w-xl px-4 py-6 sm:px-6">
        {/* Error from callback */}
        {searchParams.error && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="size-4 shrink-0" />
            <span>GitHub installation failed. Please try again.</span>
          </div>
        )}

        {/* Source selector */}
        <div className="mb-4 flex items-center gap-2">
          <Button
            size="sm"
            variant={sourceType === "GITHUB" ? "default" : "outline"}
            onClick={() => {
              setSourceType("GITHUB");
              setStep(1);
            }}
          >
            <Github className="size-4" />
            GitHub (sync)
          </Button>
          <Button
            size="sm"
            variant={sourceType === "CLOUD" ? "default" : "outline"}
            onClick={() => {
              setSourceType("CLOUD");
              setStep(1);
              setSelectedRepo(null);
              setSelectedInstallationId(null);
              setSelectedFolder("/");
            }}
          >
            Cloud project
          </Button>
          <Button
            size="sm"
            variant={sourceType === "GOOGLE_DRIVE" ? "default" : "outline"}
            onClick={() => {
              setSourceType("GOOGLE_DRIVE");
              setStep(1);
              setSelectedRepo(null);
              setSelectedInstallationId(null);
              setSelectedFolder("/");
            }}
          >
            <FolderOpen className="size-4" />
            Google Drive
          </Button>
        </div>

        {/* Step indicator */}
        <div className="mb-6 text-sm text-muted-foreground">
          {sourceType === "GITHUB" || sourceType === "GOOGLE_DRIVE"
            ? `Step ${step} of 2`
            : "Create a cloud project"}
        </div>

        {/* Step content */}
        <div className="mb-6">
          {sourceType === "GITHUB" &&
            step === 1 &&
            (hasInstallations ? (
              <StepConnectRepo
                installations={githubInstallationState.installations}
                selectedRepo={selectedRepo}
                onSelect={handleSelectRepo}
                organizationId={organizationId as Id<"workspaces">}
              />
            ) : (
              <InstallGitHubApp
                organizationId={organizationId as Id<"workspaces">}
              />
            ))}
          {sourceType === "GITHUB" &&
            step === 2 &&
            selectedRepo &&
            selectedInstallationId != null && (
              <StepChooseFolderCreate
                repo={selectedRepo}
                installationId={selectedInstallationId}
                selectedFolder={selectedFolder}
                onSelectFolder={setSelectedFolder}
                projectName={projectName}
                onNameChange={setProjectName}
              />
            )}
          {sourceType === "GOOGLE_DRIVE" &&
            step === 1 &&
            (hasDriveConnections ? (
              <StepConnectGoogleDrive
                connections={driveConnections}
                selectedConnectionId={selectedDriveConnection?.id ?? null}
                onSelect={(connection) => {
                  setSelectedDriveConnection(connection);
                  setSelectedDriveFolder(null);
                  setProjectName(connection.displayName || "Drive Project");
                }}
                organizationId={organizationId as Id<"workspaces">}
              />
            ) : (
              <InstallGoogleDrive
                organizationId={organizationId as Id<"workspaces">}
              />
            ))}
          {sourceType === "GOOGLE_DRIVE" &&
            step === 2 &&
            selectedDriveConnection && (
              <StepChooseDriveFolderCreate
                connectionId={selectedDriveConnection.id}
                selectedFolderId={selectedDriveFolder?.id ?? ""}
                onSelectFolder={(node) => {
                  setSelectedDriveFolder(node);
                  if (!projectName.trim()) {
                    setProjectName(node.name);
                  }
                }}
                projectName={projectName}
                onNameChange={setProjectName}
              />
            )}
          {sourceType === "CLOUD" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold">Create cloud project</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  This project is stored in the app database and supports
                  collaborative cloud syncing.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-name-cloud">Project name</Label>
                <Input
                  id="project-name-cloud"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="My Project"
                />
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <div>
            {(sourceType === "GITHUB" || sourceType === "GOOGLE_DRIVE") &&
            step > 1 ? (
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                <ChevronLeft className="size-4" />
                Back
              </Button>
            ) : (
              <Button variant="ghost" size="sm" asChild>
                <Link to={organizationRoute} params={{ orgSlug }}>
                  Cancel
                </Link>
              </Button>
            )}
          </div>
          <div>
            {(sourceType === "GITHUB" || sourceType === "GOOGLE_DRIVE") &&
            step === 1 ? (
              <Button
                size="sm"
                onClick={() => setStep(2)}
                disabled={!canProceed}
              >
                Next
                <ChevronRight className="size-4" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={!canProceed || isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Project"
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
